import { Form as AntdForm, type FormItemProps, type FormRule } from 'antd'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { createContext, type ReactNode, useContext, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { useListValidateTrigger } from './IncidentCaseV2StepForm'
import StepFieldLabel from './StepFieldLabel'
import { isBlank } from './stepValues'

type StepFieldProps = Omit<FormItemProps, 'label' | 'required'> & {
  label: ReactNode
  hint?: ReactNode
  /** A list of entries, checked only once Next has been tried. */
  isList?: boolean
  isRequired?: boolean
}

/**
 * Required, and not just spaces. antd's `whitespace` option would do the second
 * only by treating every value as text, failing dates, times and selections.
 */
const requiredRule = (message: string): FormRule => ({
  required: true,
  message,
  validator: (_, value: unknown) =>
    isBlank(value) ? Promise.reject(new Error(message)) : Promise.resolve()
})

const LabelIdContext = createContext<string | undefined>(undefined)

/**
 * The id of the enclosing field's label. A `<label for>` can't name a group of
 * controls, so a group sets `aria-labelledby` to this instead.
 */
export const useStepFieldLabelId = () => useContext(LabelIdContext)

/**
 * One field in a v2 step: the label as designed, and, when required, the
 * required rule with the shared message ahead of any rules of its own.
 */
const StepField = ({
  label,
  hint,
  isList,
  isRequired,
  rules = [],
  ...itemProps
}: StepFieldProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const message = t(incidentReportsConstants.CASE_V2_FIELD_REQUIRED)
  const labelId = useId()
  const listValidateTrigger = useListValidateTrigger()

  return (
    <LabelIdContext.Provider value={labelId}>
      <AntdForm.Item
        {...itemProps}
        {...(isList && { validateTrigger: listValidateTrigger })}
        rules={isRequired ? [requiredRule(message), ...rules] : rules}
        label={
          <StepFieldLabel
            id={labelId}
            label={label}
            hint={hint}
            isRequired={isRequired}
          />
        }
      />
    </LabelIdContext.Provider>
  )
}

export default StepField
